import type { Map as MapLibreMap } from "maplibre-gl";
import {
  deleteBasemapRegionCache,
  deleteBasemapRegionMeta,
  putBasemapRegion,
  regionCacheName,
  type BasemapRegion,
} from "./tacticalBasemapRegionsDb";

export const MAX_TILES = 2500;

export type PrefetchProgress = {
  done: number;
  total: number;
};

function lon2tile(lon: number, z: number): number {
  return Math.floor(((lon + 180) / 360) * 2 ** z);
}

function lat2tile(lat: number, z: number): number {
  const rad = (lat * Math.PI) / 180;
  return Math.floor(
    ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 2 ** z,
  );
}

export function estimateTileCount(
  west: number,
  south: number,
  east: number,
  north: number,
  zMin: number,
  zMax: number,
): number {
  let n = 0;
  for (let z = zMin; z <= zMax; z++) {
    const x0 = lon2tile(west, z);
    const x1 = lon2tile(east, z);
    const y0 = lat2tile(north, z);
    const y1 = lat2tile(south, z);
    n += (Math.abs(x1 - x0) + 1) * (Math.abs(y1 - y0) + 1);
  }
  return n;
}

export function extractRasterTileTemplate(map: MapLibreMap | null): string | null {
  if (!map) return null;
  try {
    const style = map.getStyle();
    const sources = style?.sources ?? {};
    for (const src of Object.values(sources)) {
      if (src && typeof src === "object" && "type" in src && src.type === "raster") {
        const tiles = (src as { tiles?: string[] }).tiles;
        if (tiles?.[0]) return tiles[0];
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function defaultOsmTemplate(): string {
  return "https://a.tile.openstreetmap.org/{z}/{x}/{y}.png";
}

function buildTileUrl(template: string, z: number, x: number, y: number): string {
  return template
    .replace(/\{z\}/g, String(z))
    .replace(/\{x\}/g, String(x))
    .replace(/\{y\}/g, String(y))
    .replace(/\{r\}/g, "");
}

export async function prefetchBasemapRegion(opts: {
  name: string;
  west: number;
  south: number;
  east: number;
  north: number;
  zMin: number;
  zMax: number;
  tileUrlTemplate: string;
  onProgress?: (p: PrefetchProgress) => void;
  signal?: AbortSignal;
}): Promise<BasemapRegion> {
  const { west, south, east, north, zMin, zMax, tileUrlTemplate } = opts;
  if (zMax < zMin || zMax - zMin > 4) {
    throw new Error("Диапазон зумов: zMax ≥ zMin и разница не больше 4");
  }
  const total = estimateTileCount(west, south, east, north, zMin, zMax);
  if (total > MAX_TILES) {
    throw new Error(`Слишком много тайлов (${total} > ${MAX_TILES})`);
  }
  if (total <= 0) throw new Error("Пустой набор тайлов");

  const id =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `r-${Date.now()}`;
  const cache = await caches.open(regionCacheName(id));
  let done = 0;
  opts.onProgress?.({ done, total });

  for (let z = zMin; z <= zMax; z++) {
    const x0 = Math.min(lon2tile(west, z), lon2tile(east, z));
    const x1 = Math.max(lon2tile(west, z), lon2tile(east, z));
    const y0 = Math.min(lat2tile(north, z), lat2tile(south, z));
    const y1 = Math.max(lat2tile(north, z), lat2tile(south, z));
    for (let x = x0; x <= x1; x++) {
      for (let y = y0; y <= y1; y++) {
        if (opts.signal?.aborted) {
          await caches.delete(regionCacheName(id));
          throw new Error("Отменено");
        }
        const url = buildTileUrl(tileUrlTemplate, z, x, y);
        try {
          const res = await fetch(url, { mode: "cors", credentials: "omit" });
          if (res.ok) {
            await cache.put(url, res.clone());
          }
        } catch {
          /* skip failed tile */
        }
        done += 1;
        if (done % 8 === 0 || done === total) opts.onProgress?.({ done, total });
      }
    }
  }

  const region: BasemapRegion = {
    id,
    name: opts.name.trim() || `Регион ${new Date().toLocaleString("ru-RU")}`,
    west,
    south,
    east,
    north,
    zMin,
    zMax,
    tileUrlTemplate,
    tileCount: total,
    createdAt: new Date().toISOString(),
  };
  await putBasemapRegion(region);
  return region;
}

export async function removeBasemapRegion(id: string): Promise<void> {
  await deleteBasemapRegionCache(id);
  await deleteBasemapRegionMeta(id);
}
